/**
 * Composable for generating SEO metadata across different page types
 *
 * @returns {Object} SEO metadata generation methods
 * @property {Function} getMetadata - Generate SEO metadata based on page type
 */
export const useSeo = () => {
  const { currentWebsite } = useWebsite()

  /**
   * Mapping of brand slugs to their full names
   * @type {Object<string, string>}
   */
  const brandNameMapping: { [key: string]: string } = {
    'giorgio-armani': 'Giorgio Armani',
    ea7: 'EA7',
    'emporio-armani': 'Emporio Armani',
    'armani-exchange': 'Armani Exchange',
    'armani-casa': 'Armani Casa',
  }

  /**
   * Computed current brand name based on the current website
   * @type {ComputedRef<string>}
   */
  const currentBrandName = computed(
    () => brandNameMapping[currentWebsite.value] || 'Armani'
  )
  const { ts } = useI18n()
  /**
   * Generate SEO metadata for editorial pages
   *
   * @param {Object} options - Metadata generation options
   * @param {string} [options.title=''] - Page title
   * @param {string} [options.brand=''] - Brand name
   * @returns {Object} SEO metadata with title and description
   */
  const getEditorialMeta = ({ title = '', brand = '' }: EditorialMeta) => ({
    title: `${title} | ${brand}`,
    description: ts('metaDescription.editorial', {
      title: title,
      brand: brand,
    }),
  })

  /**
   * Generate SEO metadata for Product Listing Pages (PLP)
   *
   * @param {PlpMeta} options - Metadata generation options
   * @param {string} [options.title=''] - Page title
   * @returns {Object} SEO metadata with title and description
   */
  const getPlpMeta = ({ title = '' }: PlpMeta) => ({
    title: `${title} | ${currentBrandName.value}`,
    description: ts(`metaDescription.plp.${currentWebsite.value}`, {
      title: title,
    }),
  })

  /**
   * Generate SEO metadata for Product Detail Pages (PDP)
   *
   * @param {PdpMeta} options - Metadata generation options
   * @param {string} [options.product=''] - Product name
   * @param {string} [options.collectionName=''] - Collection name
   * @param {string} [options.gender=''] - Product gender
   * @returns {Object} SEO metadata with title and description
   */
  const getPdpMeta = ({
    product = '',
    collectionName = '',
    gender = '',
  }: PdpMeta) => {
    const i18nDescriptionKey =
      !gender || gender.toLowerCase().includes('unisex')
        ? 'metaDescription.pdpUnisex'
        : 'metaDescription.pdp'

    return {
      title: `${product} | ${currentBrandName.value}`,
      description: ts(i18nDescriptionKey, {
        brand: currentBrandName.value,
        product: product,
        collectionName: collectionName,
        gender: gender.toLowerCase(),
      }),
    }
  }

  /**
   * Generate SEO metadata for brand homepage
   *
   * @param {BrandHomepageMeta} options - Metadata generation options
   * @param {string} [options.slug=''] - Page slug
   * @param {string} [options.brand=''] - Brand name
   * @returns {Object} SEO metadata with title and description
   */
  const getBrandHomepageMeta = ({
    slug = '',
    brand = '',
  }: BrandHomepageMeta) => {
    const title = convertSlugToTitle(slug)
    return {
      title: `${title} | ${brand}`,
      description: title,
    }
  }

  /**
   * Retrieve SEO metadata based on page type
   *
   * @param {ExtraData} extraData - Page-specific metadata options
   * @param {string} extraData.type - Type of page (plp, pdp, editorial, brandHomepage)
   * @returns {Object} SEO metadata with title and description
   */
  const getMetadata = (extraData: ExtraData) => {
    switch (extraData.type) {
      case 'plp':
        return getPlpMeta(extraData)
      case 'pdp':
        return getPdpMeta(extraData)
      case 'editorial':
        return getEditorialMeta(extraData)
      case 'brandHomepage':
        return getBrandHomepageMeta(extraData)
    }
  }

  return { getMetadata }
}
